import React, { useMemo } from 'react';
import { Modal, Tag, Space, Button, Typography, Alert } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { DataTable } from '../../ui';
import dayjs from 'dayjs';
import { useGetApplicationEventsQuery, ApplicationEvent } from '../../store/api/applicationsApi';

const { Text } = Typography;

interface ApplicationEventsModalProps {
  visible: boolean;
  applicationId: number | null;
  onClose: () => void;
}

const eventTypeRu: Record<string, string> = {
  created: 'Создание',
  ASSIGN_TO_ROUTE: 'Назначение в маршрут',
  UPDATE_CARGO_LOCATION: 'Изменение локации груза',
  status_changed: 'Смена статуса',
  loading_started: 'Начало погрузки',
  loading_completed: 'Погрузка завершена',
  unloading_started: 'Начало выгрузки',
  unloading_completed: 'Выгрузка завершена',
  location_updated: 'Обновление местоположения',
  document_received: 'Документ получен',
  document_signed: 'Документ подписан',
  updated: 'Обновление',
  departed: 'Отправление',
  arrived: 'Прибытие',
  payment_received: 'Оплата',
  issue_detected: 'Проблема',
  issue_resolved: 'Решение проблемы'
};

const ApplicationEventsModal: React.FC<ApplicationEventsModalProps> = ({ visible, applicationId, onClose }) => {
  const {
    data: events = [],
    isFetching,
    error
  } = useGetApplicationEventsQuery(applicationId as number, {
    skip: !visible || !applicationId
  });

  const errorText = error ? 'Ошибка загрузки истории событий заявки' : null;

  const columns: ColumnsType<ApplicationEvent> = useMemo(() => [
    {
      title: 'Дата',
      dataIndex: 'eventDate',
      key: 'eventDate',
      width: 160,
      render: (value: string) => (
        <div>
          <div>{dayjs(value).format('DD.MM.YYYY')}</div>
          <div style={{ color: 'var(--text-secondary)', fontSize: 12 }}>{dayjs(value).format('HH:mm:ss')}</div>
        </div>
      )
    },
    {
      title: 'Маршрут',
      key: 'routeId',
      width: 180,
      render: (_, record) => (
        record.routeId ? (
          <Button
            type="link"
            style={{ padding: 0 }}
            onClick={() => {
              window.location.href = `/routes?applicationId=${applicationId}`;
            }}
          >
            {record.routeNumber || `№${record.routeId}`}
          </Button>
        ) : '—'
      )
    },
    {
      title: 'Событие',
      key: 'event',
      width: 260,
      render: (_, record) => (
        <Space>
          <Tag color={record.source === 'route' ? 'blue' : 'green'}>
            {eventTypeRu[record.eventType] || record.eventType}
          </Tag>
          <Text>{record.title}</Text>
        </Space>
      )
    },
    {
      title: 'Описание',
      dataIndex: 'description',
      key: 'description',
      render: (value: string) => <Text>{value || '—'}</Text>
    }
  ], [applicationId]);

  return (
    <Modal
      title={`История событий по заявке #${applicationId || '—'}`}
      open={visible}
      onCancel={onClose}
      footer={null}
      width={1200}
      destroyOnHidden
    >
      {errorText && (
        <Alert style={{ marginBottom: 12 }} type="error" showIcon message={errorText} />
      )}
      <DataTable
        rowKey="id"
        columns={columns}
        dataSource={events}
        loading={isFetching}
        pagination={{ pageSize: 15, showSizeChanger: true, showQuickJumper: true }}
        scroll={{ x: 'max-content' }}
      />
    </Modal>
  );
};

export default ApplicationEventsModal;

